import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { vendorQueries } from '../../lib/queries';
import { T, sans, card } from './VendorDetails';
import type { VendorTab } from './VendorDetails';
import VendorRecord from './VendorRecord';

/* ── model ─────────────────────────────────────────────────────────── */
type Lifecycle = 'due_diligence' | 'active_monitoring' | 'offboarding';
type Tier = 'Low' | 'Medium' | 'High' | 'Critical';

interface Profile {
  category: string | null;
  business_units: string[];
  location: string | null;
  parent_company: string | null;
  vendor_since: string | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  contract_value: number | null;
  auto_renew: boolean;
  renewal_notice_days: number | null;
  ytd_spend: number | null;
  lifecycle: Lifecycle;
  data_sensitivity: string | null;
  business_criticality: string | null;
  system_access: string | null;
  service_description: string | null;
  tags: string[];
  next_review_date: string | null;
  inherent_risk: {
    tier: Tier | null;
    drivers: string[];
    factors: Record<'data_sensitivity' | 'business_criticality' | 'system_access', Tier | null>;
    assessed: boolean;
    review_cycle_months: number | null;
  };
  posture: { score: number | null; risk_level: string | null; last_scan: string | null };
  created_at: string | null;
  owner: string | null;
  ownerUserId: string | null;
  businessOwner: string | null;
  origin: { data_sensitivity: string | null; risk_tier: string | null; reason: string | null; requested_by: string | null; requested_at: string | null } | null;
  timeline: { id: string; user_name: string | null; action: string; details: string | null; created_at: string }[];
}

interface Props {
  vendorId: string;
  vendorName: string;
  vendorDomain: string;
  isDark: boolean;
  riskLevel?: string;
  onOpenTab?: (tab: VendorTab) => void;
}

/* ── helpers ───────────────────────────────────────────────────────── */
const apiError = (e: any, fallback: string) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
};

const parseDay = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const fmtDay = (d: Date | null) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const relative = (iso: string | null) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  if (mins < 43200) return `${Math.round(mins / 1440)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join('') || '?';

/* ── icons ─────────────────────────────────────────────────────────── */
const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  check: 'M5 12l5 5L20 7',
  close: 'M6 6l12 12M18 6 6 18',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  pin: 'M12 22s7-6.1 7-12a7 7 0 1 0-14 0c0 5.9 7 12 7 12ZM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  building: 'M3 21h18M6 21V7l6-4 6 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1',
  calendar: 'M8 2v3M16 2v3M3.5 9h17M4 5h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
  user: 'M20 21a8 8 0 1 0-16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  shield: 'M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M8 13h8M8 17h5',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  scan: 'M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10',
  flag: 'M4 22V4M4 4h13l-3 4 3 4H4',
  plus: 'M12 5v14M5 12h14',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 6l-10 7L2 6',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z',
  tag: 'M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8ZM7 7h.01',
  refresh: 'M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6',
};

/* ═════════════════════════════════════════════════════════════════════ */
/* ── dossier model (GET /vendors/{id}/dossier) ─────────────────────── */
type Tone = 'good' | 'warn' | 'bad' | 'neutral' | 'na';
interface Fact { value: string; source?: string | null; confidence?: string | null }
interface NewsItem { title: string; date: string | null; link: string | null; tag: string | null }
interface Check { key: string; label: string; value: string; detail: string | null; tone: Tone; source: string | null; confidence: string | null; items: NewsItem[] }
interface SeriesPoint { period: string; value_usd: number; formatted: string | null }
interface Dossier {
  scan: { id: string; completed_at: string; scan_type: string | null; score: number | null; risk_level: string | null; company_name: string | null; indicator_count: number } | null;
  company?: {
    employees: Fact | null; revenue: Fact | null; industry: Fact | null;
    market_presence: { score: number | null; value: string; factors: string[] } | null;
    growth: { value: string; signals: string[] } | null;
    domain_rank: { rank: number; value: string; days_observed: number | null } | null;
  };
  corporate?: {
    funding_stage: Fact | null; investors: { value: string; list: string[] } | null; parent: Fact | null;
    legal_entity: { lei: string; legal_name: string | null; jurisdiction: string | null; entity_status: string | null; registration_status: string | null; next_renewal: string | null; ultimate_parent: string | null; source: string | null } | null;
    ma_activity: { value: string; items: NewsItem[] } | null;
    ownership_stability: { score: number | null; value: string } | null;
  };
  financials?: {
    ticker: string; exchange: string | null; currency: string | null; market_cap: string | null;
    price: { value: string; change_percent: number | null; day_low: string | null; day_high: string | null; volume: number | null; avg_volume: number | null } | null;
    annual_revenue: string | null; revenue_growth_percent: number | null; revenue_history: SeriesPoint[];
    net_profit: string | null; profit_margin_percent: number | null; profit_history: SeriesPoint[];
    total_debt: string | null; debt_to_equity_percent: number | null;
    pe_ratio: number | null; forward_pe: number | null; peg_ratio: number | null; dividend_history: SeriesPoint[];
  } | null;
  screening?: Check[];
  threats?: Check[];
  reputation?: Check[];
  footprint?: {
    primary_domain: string | null; ip: string | null; asn: string | null; network: string | null; hosting: string | null;
    location: string | null; server: string | null; http2: string | null; http3: string | null;
    subdomains: number | null; fourth_parties: number; contacts: number;
  };
}
interface Research {
  summary: string | null; mode: 'ai' | 'template' | null; generated_at: string | null; stale: boolean;
  sources: { label: string; url: string | null }[];
  facts?: Record<string, { value: string; source: string; url: string | null }>;
}

const J = {
  globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10Z',
  external: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  chart: 'M3 3v18h18M7 15l4-4 3 3 5-6',
  scale: 'M12 3v18M5 7h14M5 7l-3 7a4 4 0 0 0 6 0L5 7ZM19 7l-3 7a4 4 0 0 0 6 0l-3-7M8 21h8',
  radar: 'M12 12 19 5M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 17a5 5 0 1 0 0-10',
  heart: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8Z',
  server: 'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01',
  briefcase: 'M3 7h18v13H3zM8 7V4h8v3M3 12h18',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  chevron: 'M6 9l6 6 6-6',
};

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'record', label: 'Vendor record' },
  { id: 'corporate', label: 'Corporate' },
] as const;
// Height of the app header (64) plus the sticky section nav, so a section lands just below the nav.
const NAV_OFFSET = 120;

const COUNTRY: Record<string, string> = {
  US: 'United States', IN: 'India', GB: 'United Kingdom', DE: 'Germany', FR: 'France', CA: 'Canada', AU: 'Australia', SG: 'Singapore',
  AE: 'United Arab Emirates', JP: 'Japan', CN: 'China', NL: 'Netherlands', IE: 'Ireland', CH: 'Switzerland', SE: 'Sweden', ZA: 'South Africa',
  BR: 'Brazil', MX: 'Mexico', ES: 'Spain', IT: 'Italy', HK: 'Hong Kong', IL: 'Israel', KR: 'South Korea',
};
const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s(/-])\w/g, m => m.toUpperCase());
// Registries and scanners shout ("ALFAHIVE PRIVATE LIMITED", "NO MATCH"): title-case all-caps runs, keep short acronyms (LEI, BSE).
const ACRONYMS = new Set(['LEI', 'BSE', 'NSE', 'NSI', 'OFAC', 'OFSI', 'SEC', 'SDN', 'USD', 'INR', 'EUR', 'GBP', 'HIBP', 'DNS', 'ESG', 'LLC', 'PLC', 'SAAS', 'IT', 'UK', 'US', 'AI']);
const cleanValue = (s: string) => {
  if (!/[A-Z]{3,}/.test(s)) return s.charAt(0).toUpperCase() + s.slice(1);
  const out = s.replace(/\b[A-Z][A-Z0-9&'-]+\b/g, w => (ACRONYMS.has(w) || /\d/.test(w) ? w : w[0] + w.slice(1).toLowerCase()));
  return out.charAt(0).toUpperCase() + out.slice(1);
};

/* ═════════════════════════════════════════════════════════════════════ */
export default function VendorProfile({ vendorId, vendorName, vendorDomain, isDark }: Props) {
  const queryClient = useQueryClient();

  const profileQuery = useQuery(vendorQueries.profile(vendorId));
  const dossierQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'dossier'],
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/dossier`)).data as Dossier,
  });
  // Live public-source research is expensive, so it lives outside the 'vendors' key root: change events don't refetch it.
  // Name and domain are part of the key: renaming a vendor or moving it to another domain is a different company to
  // research, and must not keep showing the old one's profile.
  const researchKey = ['company-research', vendorId, vendorDomain || '', vendorName || ''];
  const researchQuery = useQuery({
    queryKey: researchKey,
    queryFn: async () => (await api.get(`/v1/vendors/${vendorId}/company-profile`, { timeout: 70000 })).data as Research,
    staleTime: 10 * 60_000,
    retry: 0,
  });
  const profile: Profile | null = profileQuery.data ?? null;
  const dossier: Dossier | null = dossierQuery.data ?? null;
  const research: Research | null = researchQuery.data ?? null;

  const [refreshing, setRefreshing] = useState(false);
  const refreshResearch = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/v1/vendors/${vendorId}/company-profile`, { params: { refresh: true }, timeout: 70000 });
      queryClient.setQueryData(researchKey, data);
    } catch { /* keep what we have */ } finally { setRefreshing(false); }
  };

  const [activeNav, setActiveNav] = useState<string>('overview');
  const [logoFailed, setLogoFailed] = useState(false);

  // While a nav click is scrolling the page, the clicked section stays highlighted instead of whatever passes by.
  const navLock = useRef(false);
  const navLockTimer = useRef<number | undefined>(undefined);
  const releaseNavLockSoon = () => {
    window.clearTimeout(navLockTimer.current);
    navLockTimer.current = window.setTimeout(() => { navLock.current = false; }, 150);
  };

  // Highlight the section under the sticky nav: the last one whose top has scrolled past it, or the last section
  // once the page is scrolled to the bottom (a short final section can never reach the top).
  useEffect(() => {
    const onScroll = () => {
      if (navLock.current) { releaseNavLockSoon(); return; }
      const scroller = document.scrollingElement || document.documentElement;
      const atBottom = window.scrollY > 0 && window.innerHeight + window.scrollY >= scroller.scrollHeight - 2;
      let current: string = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(`vp-${s.id}`);
        if (el && el.getBoundingClientRect().top <= NAV_OFFSET + 10) current = s.id;
      }
      setActiveNav(atBottom ? SECTIONS[SECTIONS.length - 1].id : current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.clearTimeout(navLockTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier, profile]);

  /* palette */
  const c = {
    ink: isDark ? '#e2e8f0' : T.ink,
    ink2: isDark ? '#cbd5e1' : T.ink2,
    muted: isDark ? '#94a3b8' : T.slate,
    faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr,
    soft: isDark ? '#141414' : T.surface2,
    surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : T.blue,
    accentBg: isDark ? 'rgba(59,130,246,.12)' : T.blueBg,
  };
  const toneColor = (tone: Tone) => ({
    good: isDark ? '#4ADE80' : T.grn, warn: isDark ? '#FBBF24' : T.amb, bad: isDark ? '#F87171' : T.red,
    neutral: c.muted, na: c.faint,
  }[tone]);
  const toneBg = (tone: Tone) => ({
    good: isDark ? 'rgba(74,222,128,.10)' : T.grnBg, warn: isDark ? 'rgba(251,191,36,.10)' : T.ambBg, bad: isDark ? 'rgba(248,113,113,.10)' : T.redBg,
    neutral: c.soft, na: c.soft,
  }[tone]);
  const label: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: c.faint, textTransform: 'uppercase', letterSpacing: '.05em' };
  const pill = (fg: string, bg: string, bdr: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600,
    color: fg, background: bg, border: `1px solid ${bdr}`, whiteSpace: 'nowrap', fontFamily: sans,
  });
  const textBtn: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, color: c.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
  const sectionCard = (id: string, title: string, icon: string, sub: React.ReactNode, children: React.ReactNode, action?: React.ReactNode) => (
    <section id={`vp-${id}`} style={{ ...card(isDark), scrollMarginTop: 120 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: c.soft, border: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.muted, flexShrink: 0 }}>
            <Icon d={icon} size={14} />
          </span>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink }}>{title}</div>
            {sub && <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>{sub}</div>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
  const sourceTag = (source?: string | null, confidence?: string | null) => source || confidence ? (
    <span style={{ fontSize: 10.5, color: c.faint }}>
      {source}{source && confidence ? ' · ' : ''}{confidence ? `${titleCase(String(confidence))} confidence` : ''}
    </span>
  ) : null;

  /* ── loading / error ─────────────────────────────────────────────── */
  if (profileQuery.isPending || dossierQuery.isPending) {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        {[210, 48, 300, 260].map((h, i) => <div key={i} style={{ ...card(isDark), height: h, background: c.soft, opacity: 0.7 }} />)}
      </div>
    );
  }
  if (profileQuery.isError || dossierQuery.isError || !profile || !dossier) {
    return (
      <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: isDark ? '#F87171' : T.red }}>
        {apiError(profileQuery.error || dossierQuery.error, 'Could not load the vendor profile.')}{' '}
        <button style={textBtn} onClick={() => { profileQuery.refetch(); dossierQuery.refetch(); }}>Retry</button>
      </div>
    );
  }

  /* ── merged key facts: public sources first, then the scan, then your own record ── */
  const rf = research?.facts || {};
  const co = dossier.company, corp = dossier.corporate, fin = dossier.financials;
  const le = corp?.legal_entity || null;
  const pick = (...cands: (Fact & { href?: string | null } | null | undefined | false)[]) => cands.find(Boolean) || null;
  // GLEIF records are shown but never linked out to.
  const fromResearch = (key: string) => (rf[key] ? {
    value: rf[key].value, source: rf[key].source,
    href: rf[key].url && !/gleif/i.test(rf[key].url || '') ? rf[key].url : undefined,
  } : null);
  // GLEIF's registration date of the legal entity: only used when no source states a founding year, and labelled as
  // an incorporation date because a business can be older than its current legal entity.
  const incorporated = rf.incorporated ? parseDay(rf.incorporated.value) : null;
  const incorporatedFact = incorporated
    ? { value: String(incorporated.getFullYear()), source: `Incorporated ${fmtDay(incorporated)} · ${rf.incorporated!.source}`, href: rf.incorporated!.url }
    : null;
  const keyFacts: { label: string; icon: string; fact: (Fact & { href?: string | null }) | null; empty?: string }[] = [
    { label: 'Headquarters', icon: I.pin, fact: pick(fromResearch('headquarters'), fromResearch('address'), profile.location ? { value: profile.location, source: 'Your vendor record' } : null) },
    { label: 'Industry', icon: J.briefcase, fact: pick(fromResearch('industry'), co?.industry, profile.category ? { value: profile.category, source: 'Your vendor record' } : null) },
    { label: 'Founded', icon: I.calendar, fact: pick(fromResearch('founded'), incorporatedFact) },
    { label: 'Employees', icon: J.users, fact: pick(fromResearch('employees'), co?.employees) },
    // Only listed companies (market data, SEC) and large ones on Wikidata publish revenue; for the rest it is not public.
    { label: 'Annual revenue', icon: J.chart, fact: pick(fin?.annual_revenue ? { value: fin.annual_revenue, source: `Market data (${fin.ticker})` } : null, fromResearch('annual_revenue'), co?.revenue), empty: fin ? undefined : 'Not publicly disclosed' },
    { label: 'Ownership', icon: J.scale, fact: pick(fromResearch('listing'), corp?.funding_stage) },
    { label: 'Parent company', icon: I.building, fact: pick(fromResearch('parent_organization'), le?.ultimate_parent ? { value: le.ultimate_parent, source: 'GLEIF' } : null, corp?.parent, profile.parent_company ? { value: profile.parent_company, source: 'Your vendor record' } : null) },
    { label: 'Legal entity', icon: I.shield, fact: pick(le ? { value: titleCase(le.legal_name || vendorName), source: `GLEIF · LEI ${le.lei}` } : null, fromResearch('registered_legal_entity')) },
  ];
  const description = research?.summary || rf.description?.value || null;
  const displayName = dossier.scan?.company_name || vendorName;

  // The clicked button is highlighted straight away; the last section is often too short to reach the top, so the
  // target is clamped to the bottom of the page.
  const scrollTo = (id: string) => {
    const el = document.getElementById(`vp-${id}`);
    if (!el) return;
    const scroller = document.scrollingElement || document.documentElement;
    const max = Math.max(0, scroller.scrollHeight - window.innerHeight);
    const top = Math.min(max, Math.max(0, el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET));
    setActiveNav(id);
    navLock.current = true;
    releaseNavLockSoon();
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const kv = (k: string, v: React.ReactNode, sub?: React.ReactNode, empty = 'Not recorded') => (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...label, marginBottom: 5 }}>{k}</div>
      <div style={{ fontSize: 13, color: v ? c.ink : c.faint, fontWeight: v ? 500 : 400, overflowWrap: 'anywhere' }}>{v || empty}</div>
      {sub && <div style={{ marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const scanDate = dossier.scan ? new Date(dossier.scan.completed_at) : null;

  return (
    <div style={{ display: 'grid', gap: 16, fontFamily: sans }}>
      <style>{`
        .vp-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 1px; background: ${c.line}; border: 1px solid ${c.line}; border-radius: 10px; overflow: hidden; }
        .vp-two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        .vp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
        @media (max-width: 1180px) { .vp-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); } .vp-two { grid-template-columns: minmax(0, 1fr); } }
        @keyframes vpDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes vpFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @keyframes vpPulse { 0%,100% { opacity: .45; } 50% { opacity: .9; } }
        .vp-nav::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── section nav ──────────────────────────────────────────── */}
      <div className="vp-nav" style={{
        position: 'sticky', top: 64, zIndex: 5, display: 'flex', gap: 2, overflowX: 'auto', padding: 4, borderRadius: 10,
        background: isDark ? 'rgba(13,13,13,.92)' : 'rgba(255,255,255,.94)', border: `1px solid ${c.line}`, backdropFilter: 'blur(6px)', scrollbarWidth: 'none',
      }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => scrollTo(s.id)} style={{
            padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
            background: activeNav === s.id ? (isDark ? '#1f2937' : T.ink) : 'transparent', color: activeNav === s.id ? '#fff' : c.muted,
          }}>{s.label}</button>
        ))}
        {scanDate && <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 11, color: c.faint, whiteSpace: 'nowrap', padding: '0 8px' }}>Scan data from {fmtDay(scanDate)} · {dossier.scan!.indicator_count} checks</span>}
      </div>

      {/* ── Identity ─────────────────────────────────────────────── */}
      <section id="vp-overview" style={{ ...card(isDark), padding: 0, overflow: 'hidden', scrollMarginTop: 120 }}>
        <div style={{ padding: '20px 22px 18px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, border: `1px solid ${c.line}`, background: c.soft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
            {!logoFailed
              ? <img src={`https://www.google.com/s2/favicons?domain=${vendorDomain}&sz=128`} alt="" width={34} height={34} onError={() => setLogoFailed(true)} />
              : <span style={{ fontSize: 18, fontWeight: 800, color: c.muted }}>{initials(vendorName)}</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: c.ink, letterSpacing: '-.01em' }}>{displayName}</span>
              {fin && <span style={pill(c.accent, c.accentBg, isDark ? '#1e3a8a' : T.blueBdr)}>{fin.ticker}{fin.exchange ? ` · ${fin.exchange}` : ''}</span>}
              {le?.entity_status && <span style={pill(toneColor(le.entity_status === 'ACTIVE' ? 'good' : 'warn'), toneBg(le.entity_status === 'ACTIVE' ? 'good' : 'warn'), 'transparent')}><Icon d={I.check} size={10} />Registered entity</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5, color: c.muted }}>
              <a href={`https://${vendorDomain}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: c.accent, textDecoration: 'none', fontWeight: 600 }}>
                <Icon d={J.globe} size={12} />{vendorDomain}<Icon d={J.external} size={10} />
              </a>
              {le?.jurisdiction && <span>Registered in {COUNTRY[le.jurisdiction.split('-')[0]] || le.jurisdiction}</span>}
              {co?.domain_rank && <span>Web rank {co.domain_rank.value}</span>}
            </div>
            <div style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.65, color: c.ink2, maxWidth: 900 }}>
              {description ? description : researchQuery.isPending ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: c.muted, animation: 'vpPulse 1.4s ease-in-out infinite' }}>
                  <Icon d={J.radar} size={13} />Researching {vendorName} across public sources…
                </span>
              ) : <span style={{ color: c.faint }}>No public description found for this company.</span>}
            </div>
            {research ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 10, fontSize: 11, color: c.faint }}>
                <button style={{ ...textBtn, fontSize: 11, opacity: refreshing ? 0.6 : 1 }} disabled={refreshing} onClick={refreshResearch}>
                  <Icon d={I.refresh} size={11} style={{ animation: refreshing ? 'vpPulse 1s infinite' : undefined }} />{refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* key facts */}
        <div style={{ padding: '0 22px 20px' }}>
          <div className="vp-facts">
            {keyFacts.map(f => (
              <div key={f.label} style={{ background: c.surface, padding: '12px 14px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...label }}><Icon d={f.icon} size={11} />{f.label}</div>
                <div style={{ fontSize: 13.5, fontWeight: 650 as any, color: f.fact ? c.ink : c.faint, marginTop: 6, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                  {f.fact ? (f.fact.href ? <a href={f.fact.href} target="_blank" rel="noopener noreferrer" style={{ color: c.ink, textDecoration: 'none' }}>{cleanValue(f.fact.value)}</a> : cleanValue(f.fact.value))
                    : researchQuery.isPending ? <span style={{ display: 'inline-block', width: '60%', height: 12, borderRadius: 4, background: c.soft, animation: 'vpPulse 1.4s infinite' }} /> : (f.empty || 'Not found')}
                </div>
                {f.fact?.source && <div style={{ fontSize: 10.5, color: c.faint, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fact.source}</div>}
              </div>
            ))}
          </div>
        </div>

      </section>

      <VendorRecord vendorId={vendorId} profile={profile} isDark={isDark} />

      {!dossier.scan && (
        <div style={{ ...card(isDark), fontSize: 12.5, color: c.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon d={I.info} size={14} />This vendor has not been scanned yet, so corporate data will appear after its first scan.
        </div>
      )}

      {/* ── Corporate ────────────────────────────────────────────── */}
      {sectionCard('corporate', 'Corporate structure', I.building, 'Legal entity, ownership and market position', (
        <div style={{ display: 'grid', gap: 18 }}>
          <div className="vp-two">
            <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${c.line}`, background: c.soft }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={label}>Legal entity</span>
              </div>
              {le ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {kv('Legal name', titleCase(le.legal_name || ''))}
                  {kv('LEI', <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{le.lei}</span>)}
                  {kv('Jurisdiction', le.jurisdiction ? (COUNTRY[le.jurisdiction.split('-')[0]] || le.jurisdiction) : null)}
                  {kv('Status', le.entity_status ? titleCase(le.entity_status) + (le.registration_status ? ` · ${titleCase(le.registration_status)}` : '') : null)}
                  {kv('Ultimate parent', le.ultimate_parent || 'None reported')}
                  {kv('LEI renewal due', le.next_renewal ? fmtDay(parseDay(le.next_renewal)) : null)}
                </div>
              ) : rf.registered_legal_entity ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {kv('Registered entity', cleanValue(rf.registered_legal_entity.value))}
                  <div style={{ fontSize: 11.5, color: c.muted, lineHeight: 1.5 }}>
                    Found in the GLEIF register by the live lookup. The next scan adds the LEI, status and renewal details here.
                  </div>
                </div>
              ) : <div style={{ fontSize: 12.5, color: c.faint }}>No Legal Entity Identifier found for this company in the GLEIF register. Common for smaller private companies.</div>}
            </div>
            <div style={{ padding: 14, borderRadius: 10, border: `1px solid ${c.line}`, background: c.soft }}>
              <div style={{ ...label, marginBottom: 10 }}>Ownership</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {kv('Company type', corp?.funding_stage ? cleanValue(corp.funding_stage.value) : null, sourceTag(corp?.funding_stage?.source))}
                {kv('Parent', keyFacts[6].fact ? cleanValue(keyFacts[6].fact.value) : null, sourceTag(keyFacts[6].fact?.source), 'None found')}
                {kv('Investors', corp?.investors ? (corp.investors.list.length ? corp.investors.list.join(', ') : corp.investors.value) : null, null, 'None disclosed')}
                {kv('Financial stability', corp?.ownership_stability ? `${corp.ownership_stability.value}${corp.ownership_stability.score != null ? ` (${corp.ownership_stability.score}/100)` : ''}` : null)}
              </div>
            </div>
          </div>

          <div className="vp-two">
            <div>
              <div style={{ ...label, marginBottom: 8 }}>Market presence</div>
              {co?.market_presence ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 8, borderRadius: 99, background: c.soft, border: `1px solid ${c.line}`, overflow: 'hidden' }}>
                      <div style={{ width: `${co.market_presence.score || 0}%`, height: '100%', background: c.accent }} />
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: c.ink }}>{co.market_presence.score ?? '—'}/100</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: c.ink, marginTop: 6, fontWeight: 600 }}>{co.market_presence.value}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    {co.market_presence.factors.map(f => <span key={f} style={pill(c.muted, c.soft, c.line)}>{f}</span>)}
                  </div>
                </>
              ) : <div style={{ fontSize: 12.5, color: c.faint }}>No data</div>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignContent: 'start' }}>
              {kv('Growth', co?.growth?.value, co?.growth?.signals?.length ? <span style={{ fontSize: 11, color: c.faint }}>{co.growth.signals.join(', ')}</span> : null)}
              {kv('Web popularity', co?.domain_rank ? `${co.domain_rank.value} globally` : 'Not in the top 1M', co?.domain_rank?.days_observed ? <span style={{ fontSize: 11, color: c.faint }}>Tranco · seen {co.domain_rank.days_observed} days</span> : null)}
              {kv('Employees', keyFacts[3].fact ? cleanValue(keyFacts[3].fact.value) : null, sourceTag(keyFacts[3].fact?.source), 'Not found')}
              {kv('Founded', keyFacts[2].fact?.value || null, sourceTag(keyFacts[2].fact?.source), 'Not found in public sources')}
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={label}>Mergers & acquisitions</span>
              {corp?.ma_activity && <span style={{ fontSize: 11.5, color: c.muted }}>{corp.ma_activity.value}</span>}
            </div>
            {corp?.ma_activity?.items.length ? (
              <div style={{ display: 'grid', gap: 6 }}>
                {corp.ma_activity.items.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.line}`, fontSize: 12.5 }}>
                    {m.date && <span style={{ fontSize: 11, color: c.faint, whiteSpace: 'nowrap', minWidth: 70 }}>{m.date}</span>}
                    <span style={{ color: c.ink }}>{m.link ? <a href={m.link} target="_blank" rel="noopener noreferrer" style={{ color: c.ink }}>{m.title}</a> : m.title}</span>
                  </div>
                ))}
              </div>
            ) : <div style={{ fontSize: 12.5, color: c.faint }}>No recent merger or acquisition news.</div>}
          </div>
        </div>
      ))}

    </div>
  );
}
